import React, { useEffect, useMemo, useState } from 'react';
import { Header, Link, Table, TableColumn } from '@backstage/core-components';
import { useApi, discoveryApiRef, identityApiRef } from '@backstage/core-plugin-api';
import axios from 'axios';
import { makeStyles, Button, FormControl, InputLabel, Select, MenuItem } from '@material-ui/core';
import Loader from '../loader/Loader';
import { useNavigate } from 'react-router';

const useStyles = makeStyles({
  container: {
    padding: '1px 40px',
  },
  emptyRows: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '60vh',
    fontSize: '1.3rem',
    fontWeight: 'bold',
    color: '#555',
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginRight: '38px',
    padding: '30px 1px 16px',
  },
});

export function RegisterApiButton() {
  const navigate = useNavigate();
  return (
    <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '40px 40px 16px 40px' }}>
      <Button
        variant="contained"
        color="primary"
        onClick={() => navigate('/catalog-import')}
        style={{ marginBottom: 16 }}
      >
        Register Existing API
      </Button>
    </div>
  );
}

export default function TenantApiExplorerPage() {
  const styles = useStyles();
  const containerStyle = useMemo(() => ({ width: '100%', height: '100vh' }), []);
  const discoveryApi = useApi(discoveryApiRef);
  const identityApi = useApi(identityApiRef);

  const [apis, setApis] = useState<any[]>([]);
  const [filteredApis, setFilteredApis] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [org, setOrg] = useState('');
  const [role, setRole] = useState('');

  const [tenants, setTenants] = useState<{ id: number; name: string }[]>([]);
  const [selectedTenant, setSelectedTenant] = useState<string>('all');

  const formatData = (entities: any[]) => {
    return entities.map(entity => ({
      name: entity.metadata.name,
      description: entity.metadata.description || '-',
      owner: entity.spec?.owner || '-',
      lifecycle: entity.spec?.lifecycle || '-',
      system: entity.spec?.system || '-',
      entity,
    }));
  };

  const fetchTenantsFromDb = async () => {
    try {
      const baseUrl = await discoveryApi.getBaseUrl('user');
      const response = await axios.get(`${baseUrl}/tenants`);
      setTenants(response.data);
      return response.data;
    } catch (e) {
      console.error('Error fetching tenants:', e);
      return [];
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);

      const details = await identityApi.getBackstageIdentity();
      const tenantId = details.ownershipEntityRefs.find(ref =>
        ref.startsWith('tenantId:'),
      )?.split(':')[1];

      const userRole = details.ownershipEntityRefs.find(ref =>
        ref.startsWith('role:')
      )?.split(':')[1];

      setRole(userRole);

      const baseUrl = await discoveryApi.getBaseUrl('catalog');
      let response: any;
      if (userRole === 'SuperAdmin') {
        response = await axios.get(`${baseUrl}/entities?filter=kind=API`);
      } else {
        response = await axios.get(
          `${baseUrl}/entities?filter=kind=API,metadata.annotations.tenant.com/tenant-id=${tenantId}`,
        );
      }

      const filtered = response.data.filter((entity: any) => {
        const annotations = entity?.metadata?.annotations || {};
        return annotations['tenant.com/tenant-id'];
      });

      const tenantsFromDb = await fetchTenantsFromDb();
      const tenant = tenantsFromDb.find(t => t.id.toString() === tenantId);
      setOrg(tenant.name);

      const enrichedResponse = filtered.map((entity: any) => {
        const annotations = entity?.metadata?.annotations || {};
        const entityTenantId = parseInt(annotations['tenant.com/tenant-id'], 10);

        const matchedTenant = tenantsFromDb.find((t: any) => t.id === entityTenantId);

        return {
          ...entity,
          tenantName: matchedTenant ? matchedTenant.name : 'Unknown',
        };
      });

      // unique tenants
      const uniqueTenants = Array.from(
        new Map(
          enrichedResponse.map((entity: any) => {
            const id = parseInt(entity.metadata.annotations['tenant.com/tenant-id'], 10);
            return [id, { id, name: entity.tenantName }];
          }),
        ).values(),
      );

      // setTenants(uniqueTenants);

      const formatted = formatData(enrichedResponse);
      setApis(formatted);
      setFilteredApis(formatted);

      setLoading(false);
    } catch (error) {
      console.error('Error fetching tenant APIs:', error);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    const orgName = localStorage.getItem('orgName');
    if (orgName) {
      setOrg(orgName);
    }
  }, []);

  const handleTenantChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const tenantId = event.target.value as string;
    setSelectedTenant(tenantId);

    if (tenantId === 'all') {
      setFilteredApis(apis);
      // setOrg('All Tenants');
    } else {
      setFilteredApis(
        apis.filter(wf => wf.entity.metadata.annotations['tenant.com/tenant-id'] === tenantId),
      );
      // const tenant = tenants.find(t => t.id.toString() === tenantId);
      // if (tenant) {
      //   setOrg(tenant.name);
      // }
    }
  };

  const columns: TableColumn[] = [
    {
      title: 'Name',
      field: 'name',
      render: ({ entity }) => (
        <Link to={`/catalog/default/api/${entity?.metadata?.name}`}>
          {entity?.metadata?.name}
        </Link>
      ),
    },
    { title: 'Description', field: 'description' },
    { title: 'Owner', field: 'owner' },
    { title: 'Lifecycle', field: 'lifecycle' },
    { title: 'System', field: 'system' },
  ];

  return (
    <div style={containerStyle}>
      <Header title="API Explorer" subtitle={org} />

      <div className={styles.buttonContainer}>
        <RegisterApiButton />
        {role === 'SuperAdmin' && (
          <div>
            <FormControl variant="outlined" size="small" style={{ minWidth: 220 }}>
              <InputLabel id="tenant-select-label"></InputLabel>
              <Select
                labelId="tenant-select-label"
                value={selectedTenant}
                onChange={handleTenantChange}
                displayEmpty
              >
                <MenuItem value="all">All Tenants</MenuItem>
                {tenants.map(tenant => (
                  <MenuItem key={tenant.id} value={tenant.id.toString()}>
                    {tenant.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
        )}
      </div>

      <div className={styles.container}>
        {loading ? (
          <Loader />
        ) : filteredApis.length > 0 ? (
          <Table options={{ paging: false }} data={filteredApis} columns={columns} title="APIs" />
        ) : (
          <div className={styles.emptyRows}>
            No APIs found for this tenant. Please Register
          </div>
        )}
      </div>
    </div>
  );
  
}
